import { JobItemSorBulkActionId, JobItemStatusBulkActionId } from '@jms/model';
import type { Command } from '@workbuddy/ui/model';
import type { EntityBoardBulkCommandSection } from '@workbuddy/ui/v2/blocks/entity.board/context';

interface SorBulkPlace {
  section: string;
  /** The palette's name when it differs from the descriptor's, which the Items tab still shows. */
  label?: string;
}

/**
 * The SORs tab's ⌘ Actions, in the ticket's sections and order (WBS-12433). The labels drop "(Pending lines)" /
 * "(Authorised lines)" because the section heading now says it. The order here is the palette's: the commands arrive
 * sorted by label from the shared model, so only this list decides the order within a section.
 */
const SOR_BULK_PLACES = new Map<string, SorBulkPlace>([
  ['items.comment.add', { section: 'General' }],
  ['items.evidence.add', { section: 'General' }],
  [JobItemSorBulkActionId.Complete, { section: 'Authorised Lines' }],
  [JobItemSorBulkActionId.Resolve, { section: 'Authorised Lines' }],
  [JobItemSorBulkActionId.Reopen, { section: 'Authorised Lines' }],
  [JobItemStatusBulkActionId.AuthorisedChangeStatus, { section: 'Authorised Lines', label: 'Change Status' }],
  [JobItemStatusBulkActionId.AuthorisedCancel, { section: 'Authorised Lines', label: 'Cancel' }],
  [JobItemSorBulkActionId.Request, { section: 'Pending Lines' }],
  ['items.pending.approve', { section: 'Pending Lines', label: 'Approve SORs' }],
  [JobItemSorBulkActionId.Decline, { section: 'Pending Lines' }],
  [JobItemStatusBulkActionId.PendingChangeStatus, { section: 'Pending Lines', label: 'Change Status' }],
  [JobItemStatusBulkActionId.PendingCancel, { section: 'Pending Lines', label: 'Cancel' }],
  [JobItemSorBulkActionId.DeleteDraft, { section: 'Pending Lines' }],
]);

const SECTIONS = ['General', 'Authorised Lines', 'Pending Lines'];

/**
 * Lays the SORs tab's palette out in the ticket's sections. A command the ticket does not name (Move to stage, Create
 * Purchase Order) keeps today's place: first, under "Actions", in the order it arrives.
 */
export function groupSorBulkCommands(commands: readonly Command[]): EntityBoardBulkCommandSection[] {

  const others = commands.filter(command => !SOR_BULK_PLACES.has(command._id));
  const placed = [...SOR_BULK_PLACES].flatMap(([id, place]) => {
    const command = commands.find(candidate => candidate._id === id);
    if (!command) return [];

    return [{ section: place.section, command: place.label ? command.clone({ label: place.label }) : command }];
  });

  return [
    ...(others.length ? [{ label: 'Actions', commands: others }] : []),
    ...SECTIONS.flatMap(section => {
      const inSection = placed.filter(entry => entry.section === section).map(entry => entry.command);
      return inSection.length ? [{ label: section, commands: inSection }] : [];
    }),
  ];
}
